import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import generalTranslationsDe from './i18n/de';
import generalTranslationsEn from './i18n/en';
import feTranslationsDe from './modules/fe/i18n/de';
import feTranslationsEn from './modules/fe/i18n/en';
import _upperFirst from 'lodash/upperFirst';
import _camelCase from 'lodash/camelCase';
import './polyfills/globalThis';

window.__ = (key, data = null) => i18n.global.t(key, data);
window.axios = axios;

const app = createApp({
    data() {
        return {
            navOpen: false,
        };
    },
    methods: {},
});

const componentFiles = import.meta.glob(['./components/*.vue', './modules/fe/components/*.vue'], {
    eager: true,
});

Object.entries(componentFiles).forEach(([path, m]) => {
    const componentName = _upperFirst(
        _camelCase(
            path
                .split('/')
                .pop()
                .replace(/\.\w+$/, '')
        )
    );

    app.component(componentName, m.default);
});

const numberFormats = {
    en: {
        currency: {
            style: 'currency',
            currency: 'EUR',
            notation: 'standard',
            currencyDisplay: 'symbol',
        },
    },
    de: {
        currency: {
            style: 'currency',
            currency: 'EUR',
            useGrouping: true,
            currencyDisplay: 'symbol',
        },
    },
};

const i18n = createI18n({
    numberFormats,
    legacy: false,
    globalInjection: true,
    locale: document.documentElement.lang,
    fallbackLocale: document.documentElement.lang === 'en' ? 'de' : 'en',
    messages: {
        de: Object.assign(generalTranslationsDe, feTranslationsDe, {}),
        en: Object.assign(generalTranslationsEn, feTranslationsEn, {}),
    },
});

if (import.meta.env.VITE_VUE_SENTRY_DSN) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_VUE_SENTRY_DSN,
        ignoreErrors: [
            /^Network Error$/,
            'Request failed with status code 401',
            'Invalid call to runtime.sendMessage(). Tab not found.',
            // Cross-origin iframe access errors from third-party scripts (Reddit, GTM, etc.)
            // These are expected browser security restrictions, not actual errors
            "Failed to read a named property 'Element' from 'Window': Blocked a frame with origin",
            // Reddit tracking pixel errors (minified/obfuscated)
            /^Oa$/,
            /^Error: Oa$/,
            // Reddit pixel localStorage errors (old browsers or disabled storage)
            /Cannot read properties of null \(reading 'getItem'\).*ads\/pixel/,
            /TypeError.*Cannot read properties of null \(.*\).*getItem.*redditstatic/,
            // Safari internal messaging errors from privacy features (ITP)
            'Unhandled error response received for message <shell-plugins-site-config>',
            'Unhandled error response received for message <get-frame-manager-configuration>',
            // Axios request aborted errors (expected when components unmount during navigation)
            /^AxiosError: Request aborted$/,
            'Request aborted',
        ],
        beforeSend(event) {
            try {
                // Check if this is a recursion error from Sentry itself
                if (event.message && event.message.includes('Maximum call stack size exceeded')) {
                    return null;
                }
                // Filter out errors from Chrome extensions
                if (event.location && event.location.startsWith('chrome-extension://')) {
                    return null;
                }
                if (event.culprit && event.culprit.includes('chrome-extension://')) {
                    return null;
                }
                if (event.exception?.values) {
                    for (const exception of event.exception.values) {
                        if (exception.stacktrace?.frames) {
                            for (const frame of exception.stacktrace.frames) {
                                if (
                                    frame.filename?.startsWith('chrome-extension://') ||
                                    frame.abs_path?.startsWith('chrome-extension://')
                                ) {
                                    return null;
                                }
                            }
                        }
                    }
                }
                return event;
            } catch {
                return null;
            }
        },
        maxBreadcrumbs: 50,
    });

    try {
        Sentry.setTag('product', 'Deck');
    } catch (error) {
        console.warn('Failed to set Sentry tag:', error);
    }
}

app.directive('click-outside', {
    mounted: function mounted(el, binding) {
        el.clickOutsideEvent = (event) => {
            if (!(el === event.target || el.contains(event.target))) {
                if (typeof binding.value === 'function') {
                    binding.value(event, el);
                } else {
                    console.info('[click-outside] Value have to be a function');
                }
            }
        };
        document.addEventListener('mousedown', el.clickOutsideEvent);
    },
    unmounted: function unmounted(el) {
        document.removeEventListener('mousedown', el.clickOutsideEvent);
    },
});

app.use(createPinia()).use(i18n);
app.mount('#app');

addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('.header');
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    if (header && ((month === 12 && day >= 30) || (month === 1 && day <= 1))) {
        for (let i = 0; i < 10; i++) {
            const firework = document.createElement('div');
            firework.classList.add('firework');
            firework.style.animationDelay = `${Math.random() * 5}s`;
            firework.style.left = `${Math.random() * 100}%`;
            firework.style.top = `${Math.random() * 100}%`;
            header.appendChild(firework);
        }
    }
});
